import { CurrencyEnum, HeadlessUI, centsToUnits } from '@systemeio/ui'
import { useAffiliateDashboardVendors } from 'modules/affiliate-dashboard/hooks/use-affiliate-dashboard-vendors'
import { AffiliateDashboardFilterInterface } from 'modules/affiliate-dashboard/types/affiliate-dashboard-interface'
import {
  AffiliateDashboardVendorsInterface,
  VendorId,
} from 'modules/affiliate-dashboard/types/affiliate-dashboard-vendors-interface'
import React, { Fragment, useEffect, useState } from 'react'
import { Loader } from 'shared/components/loader'
import { Table } from 'shared/components/table/table'
import { ColumnDefinitionInterface } from 'shared/components/table/types/column-interface'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useUser from 'shared/hooks/use-user'
import ArrowDown from 'shared/icons/arrow-down-icon'
import CloseIcon from 'shared/icons/close-icon'
import { currencyFormat } from 'shared/utils/currency-format'

interface AffiliateVendorSelectorProps {
  selectedVendor?: VendorId
  setFilter: React.Dispatch<React.SetStateAction<AffiliateDashboardFilterInterface>>
}

const AffiliateDashboardVendorSelector = ({
  selectedVendor,
  setFilter,
}: AffiliateVendorSelectorProps) => {
  const { t } = useLocoTranslation()
  const { user } = useUser()

  const [shouldFetch, setShouldFetch] = useState(false)

  useEffect(() => {
    if (selectedVendor) {
      setShouldFetch(true)
    }
  }, [selectedVendor])

  const { data: vendors } = useAffiliateDashboardVendors(shouldFetch)

  const selectedVendorName = vendors?.find(el => el.vendor.id === selectedVendor)?.vendor
    .affiliateProgramBrandName

  const columns: ColumnDefinitionInterface<AffiliateDashboardVendorsInterface>[] = [
    {
      key: 'vendor',
      header: t('dashboard.affiliate_dashboard.vendor'),
      widthPercent: 30,
      onRender: vendor => (
        <div className={'w-full truncate'}>
          <span
            className={`${
              vendor.vendor.id === selectedVendor ? 'text-blue' : 'text-darkblue'
            } truncate w-full`}
          >
            {vendor.vendor.affiliateProgramBrandName}
          </span>
        </div>
      ),
    },
    {
      key: 'sales',
      header: t('dashboard.affiliate.lifetime_earnings'),
      widthPercent: 30,
      onRender: vendor => (
        <div className={'flex flex-col gap-1.5'}>
          {Object.keys(vendor.sales).map((el, idx) => {
            const cur = el as CurrencyEnum
            return (
              <span
                className={`${
                  vendor.vendor.id === selectedVendor ? 'text-blue' : 'text-darkblue'
                } `}
                key={idx}
              >
                {currencyFormat(centsToUnits(vendor.sales[cur]), cur, user?.dashboardLocale)}
              </span>
            )
          })}
        </div>
      ),
    },
    {
      key: 'sales',
      widthPercent: 40,
      header: (
        <div className={'flex gap-1'}>
          <span className={'text-darkblue'}>{`${t('dashboard.affiliate.paid')} /`}</span>
          <span className={'text-darkblue/50'}>{t('dashboard.affiliate.pending')}</span>
        </div>
      ),
      onRender: vendor => (
        <div className={'flex flex-col gap-1.5'}>
          {Object.keys(vendor.paid).map((el, idx) => {
            const cur = el as CurrencyEnum
            return (
              <div key={idx} className={'flex gap-1'}>
                <span
                  className={`${
                    vendor.vendor.id === selectedVendor ? 'text-blue' : 'text-darkblue'
                  } `}
                >
                  {`${currencyFormat(
                    centsToUnits(vendor.paid[cur]),
                    cur,
                    user?.dashboardLocale,
                  )} /`}
                </span>
                <span
                  className={`${
                    vendor.vendor.id === selectedVendor ? 'text-blue/50' : 'text-darkblue/50'
                  } `}
                >
                  {currencyFormat(centsToUnits(vendor.pending[cur]), cur, user?.dashboardLocale)}
                </span>
              </div>
            )
          })}
        </div>
      ),
    },
  ]

  return (
    <HeadlessUI.Popover className="relative w-full">
      {({ open }) => (
        <>
          <HeadlessUI.PopoverButton
            className={`truncate lg:min-w-[200px] flex gap-3 justify-between items-center w-full py-2.5 pl-4 ${
              selectedVendor ? 'pr-16' : 'pr-10'
            } rounded-lg bg-white text-darkblue main-transition-colors text-left text-sm border ${
              open ? 'border-blue' : 'border-gray/30'
            } ${
              open && 'border-blue'
            } focus:outline-none focus-visible:border-blue text-darkblue placeholder:text-gray-300/70`}
          >
            {selectedVendor ? (
              selectedVendorName ? (
                <span className={'truncate'}>{selectedVendorName}</span>
              ) : (
                <Loader small />
              )
            ) : (
              <span>{t('dashboard.affiliate_dashboard.all_vendors')}</span>
            )}
            {selectedVendor && (
              <div
                onClick={e => {
                  e.stopPropagation()
                  setFilter(prev => ({ ...prev, vendorId: undefined }))
                }}
                className={`absolute inset-y-0 right-0 flex items-center pr-10`}
              >
                <CloseIcon className={`cursor-pointer fill-darkblue`} />
              </div>
            )}
            <div className="absolute inset-y-0 right-0 flex items-center pr-4">
              <ArrowDown
                className={`cursor-pointer stroke-darkblue transition-transform duration-300 ${
                  open && 'rotate-180'
                }`}
                aria-hidden="true"
              />
            </div>
          </HeadlessUI.PopoverButton>
          <HeadlessUI.Transition
            beforeEnter={() => setShouldFetch(true)}
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <HeadlessUI.PopoverPanel className="absolute right-0 z-10 mt-1.5 w-screen max-w-sm transform px-4 sm:px-0 lg:max-w-3xl">
              {({ close }) => (
                <div className="overflow-hidden bg-white customScroll max-h-[calc(100vh-350px)]  sm:max-h-[calc(100vh-200px)] overflow-y-auto rounded-lg shadow-lg ring-1 ring-black ring-opacity-5">
                  <Table
                    nestedId={'vendor'}
                    onRowClick={vendor => {
                      setFilter(prev => ({ ...prev, vendorId: vendor.vendor.id }))
                      close()
                    }}
                    data={vendors && { hasMore: false, items: vendors }}
                    columns={columns}
                  />
                </div>
              )}
            </HeadlessUI.PopoverPanel>
          </HeadlessUI.Transition>
        </>
      )}
    </HeadlessUI.Popover>
  )
}

export default AffiliateDashboardVendorSelector
